import { httpCoreService } from "../services/HttpCoreService";
import type { InvestigatorStatus } from "@/types/investigatorStatus.type";
import type { ApiResponse } from "../types/auth.type";

export interface PrincipalInvestigator {
  id: string;
  siteId: string;
  name: string;
  email?: string;
  phone?: string;
  isActive: boolean;
  status?: InvestigatorStatus;
  role?: string;
  qualification?: string;
  specialty?: string;
  licenseNo?: string;
  licenseExpiry?: string;
  gcpExpiry?: string;
  cvExpiry?: string;
  cvUploaded?: boolean;
  tenantId: string;
  createdAt: string;
  updatedAt: string;
}

export interface StudySitePiAssignment {
  id: string;
  studySiteAssignmentId: string;
  siteContactId?: string;
  siteContact?: PrincipalInvestigator;
  principalInvestigatorId: string;
  principalInvestigator: PrincipalInvestigator;
  assignedAt: string;
}

export interface InvestigatorType {
  id: string;
  name: string;
  description?: string;
  displayOrder: number;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface CreatePiRequest {
  name: string;
  email?: string;
  phone?: string;
  role?: string;
  qualification?: string;
  specialty?: string;
  licenseNo?: string;
  licenseExpiry?: string;
  gcpExpiry?: string;
  cvExpiry?: string;
  cvUploaded?: boolean;
  status?: InvestigatorStatus;
}

export interface UpdatePiRequest {
  name?: string;
  email?: string;
  phone?: string;
  isActive?: boolean;
  status?: InvestigatorStatus;
  role?: string;
  qualification?: string;
  specialty?: string;
  licenseNo?: string;
  licenseExpiry?: string;
  gcpExpiry?: string;
  cvExpiry?: string;
  cvUploaded?: boolean;
  linkedSiteId?: string;
}

export interface IPrincipalInvestigatorRepository {
  getInvestigatorTypes(includeInactive?: boolean): Promise<InvestigatorType[]>;
  getPisForSite(
    siteId: string,
    includeInactive?: boolean,
  ): Promise<PrincipalInvestigator[]>;
  createPi(
    siteId: string,
    data: CreatePiRequest,
  ): Promise<PrincipalInvestigator>;
  updatePi(
    siteId: string,
    piId: string,
    data: UpdatePiRequest,
  ): Promise<PrincipalInvestigator>;
  linkPiToSite(
    siteId: string,
    piId: string,
    targetSiteId: string,
    makePrimary?: boolean,
  ): Promise<PrincipalInvestigator>;
  deletePi(siteId: string, piId: string): Promise<void>;
  getPisForStudySite(
    siteId: string,
    assignmentId: string,
  ): Promise<StudySitePiAssignment[]>;
  assignPiToStudySite(
    siteId: string,
    assignmentId: string,
    piId: string,
  ): Promise<StudySitePiAssignment>;
  unassignPiFromStudySite(
    siteId: string,
    assignmentId: string,
    piId: string,
  ): Promise<void>;
}

export class PrincipalInvestigatorRepository implements IPrincipalInvestigatorRepository {
  async getInvestigatorTypes(
    includeInactive = false,
  ): Promise<InvestigatorType[]> {
    const url = `/investigator-types${includeInactive ? "?includeInactive=true" : ""}`;
    const response: ApiResponse<InvestigatorType[]> =
      await httpCoreService.get<InvestigatorType[]>(url);

    return response.data ?? [];
  }

  async getPisForSite(
    siteId: string,
    includeInactive = false,
  ): Promise<PrincipalInvestigator[]> {
    const query = includeInactive ? "?includeInactive=true" : "";
    const response: ApiResponse<PrincipalInvestigator[]> =
      await httpCoreService.get<PrincipalInvestigator[]>(
        `/sites/${siteId}/site-contacts${query}`,
      );
    return response.data ?? [];
  }

  async createPi(
    siteId: string,
    data: CreatePiRequest,
  ): Promise<PrincipalInvestigator> {
    const response: ApiResponse<PrincipalInvestigator> =
      await httpCoreService.post<PrincipalInvestigator, CreatePiRequest>(
        `/sites/${siteId}/site-contacts`,
        data,
      );

    if (!response.data) {
      throw new Error("Failed to create Site Contact");
    }

    return response.data;
  }

  async updatePi(
    siteId: string,
    piId: string,
    data: UpdatePiRequest,
  ): Promise<PrincipalInvestigator> {
    const response: ApiResponse<PrincipalInvestigator> =
      await httpCoreService.patch<PrincipalInvestigator, UpdatePiRequest>(
        `/sites/${siteId}/site-contacts/${piId}`,
        data,
      );

    if (!response.data) {
      throw new Error("Failed to update Site Contact");
    }

    return response.data;
  }

  async linkPiToSite(
    siteId: string,
    piId: string,
    targetSiteId: string,
    makePrimary = false,
  ): Promise<PrincipalInvestigator> {
    const response: ApiResponse<PrincipalInvestigator> =
      await httpCoreService.post<
        PrincipalInvestigator,
        { targetSiteId: string; makePrimary?: boolean }
      >(`/sites/${siteId}/site-contacts/${piId}/link-site`, {
        targetSiteId,
        makePrimary,
      });

    if (!response.data) {
      throw new Error("Failed to link Site Contact to site");
    }

    return response.data;
  }

  async deletePi(siteId: string, piId: string): Promise<void> {
    await httpCoreService.delete<unknown>(
      `/sites/${siteId}/site-contacts/${piId}`,
    );
  }

  async getPisForStudySite(
    siteId: string,
    assignmentId: string,
  ): Promise<StudySitePiAssignment[]> {
    const response: ApiResponse<StudySitePiAssignment[]> =
      await httpCoreService.get<StudySitePiAssignment[]>(
        `/sites/${siteId}/study-site-assignments/${assignmentId}/site-contacts`,
      );

    return (response.data ?? []).map((row) => ({
      ...row,
      siteContactId: row.siteContactId ?? row.principalInvestigatorId,
      principalInvestigatorId:
        row.principalInvestigatorId ?? row.siteContactId ?? "",
      siteContact: row.siteContact ?? row.principalInvestigator,
      principalInvestigator:
        row.principalInvestigator ??
        row.siteContact ??
        ({} as PrincipalInvestigator),
    }));
  }

  async assignPiToStudySite(
    siteId: string,
    assignmentId: string,
    piId: string,
  ): Promise<StudySitePiAssignment> {
    const response: ApiResponse<StudySitePiAssignment> =
      await httpCoreService.post<
        StudySitePiAssignment,
        { siteContactId: string }
      >(
        `/sites/${siteId}/study-site-assignments/${assignmentId}/site-contacts`,
        { siteContactId: piId },
      );

    if (!response.data) {
      throw new Error("Failed to assign Site Contact to study-site");
    }

    return response.data;
  }

  async unassignPiFromStudySite(
    siteId: string,
    assignmentId: string,
    piId: string,
  ): Promise<void> {
    await httpCoreService.delete<unknown>(
      `/sites/${siteId}/study-site-assignments/${assignmentId}/site-contacts/${piId}`,
    );
  }
}
